import { useState, useRef, useEffect, useCallback } from "react";
import { AudioVisualizer } from "react-audio-visualize";
import { Play, Square } from "lucide-react";

export const AudioWaveformPlayer = ({
  src,
  height = 32,
}: {
  src: string;
  height?: number;
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [width, setWidth] = useState(200);

  useEffect(() => {
    let cancelled = false;
    fetch(src)
      .then((res) => res.blob())
      .then((b) => { if (!cancelled) setBlob(b); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [src]);

  const updateWidth = useCallback(() => {
    if (containerRef.current) {
      setWidth(containerRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [updateWidth]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      audio.currentTime = 0;
      setCurrentTime(0);
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <audio
        ref={audioRef}
        src={src}
        onEnded={() => { setIsPlaying(false); setCurrentTime(0); }}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
      />
      <button
        onClick={toggle}
        className="shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
      >
        {isPlaying
          ? <Square size={12} className="fill-current" />
          : <Play size={12} className="fill-current ml-0.5" />
        }
      </button>
      <div ref={containerRef} className="flex-1 min-w-0 cursor-pointer" onClick={toggle}>
        {blob ? (
          <AudioVisualizer
            blob={blob}
            width={width}
            height={height}
            barWidth={2}
            gap={1}
            barColor="#cbd5e1"
            barPlayedColor="#6366f1"
            currentTime={currentTime}
          />
        ) : (
          <div className="h-8 rounded bg-slate-100 animate-pulse" />
        )}
      </div>
    </div>
  );
};
